import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Textarea } from "@baluu/ui/components/textarea";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice } from "../parts";
import { isOtherSchool } from "../school";
import { RatingInput } from "./review/ReviewForm";
import {
  choicesFor,
  emptySubmitReview,
  submitReviewPayload,
  takesReviews,
  validateSubmitReview,
  type ReviewTargetType,
  type SubmitReviewErrors,
  type SubmitReviewPage,
  type SubmitReviewValues,
} from "./review/submitReview";

// /submit-review: the school's own "leave us a review" form, the link its
// dashboard shares. Port of v1's SubmitReviewView (GET
// /widget/pages/review/:slug, POST /widget/:slug/submit-review). The
// thank-you shows in place (v1 went to /submit-review/thanks).

const FAILED = "Something went wrong. Please try again.";

export function SubmitReviewView({ ctx }: { ctx: ViewContext }) {
  const slug = ctx.businessSlug.trim();
  const page = useQuery({
    queryKey: ["booking", "submit-review", ctx.api, slug],
    queryFn: () => bookingJson<SubmitReviewPage>(`${ctx.api}/widget/pages/review/${encodeURIComponent(slug)}`),
    enabled: !!slug,
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });
  const submit = useMutation({
    mutationFn: (values: SubmitReviewValues) =>
      sendJson(`${ctx.api}/widget/${encodeURIComponent(slug)}/submit-review`, "POST", submitReviewPayload(values)),
  });

  const missing = <BookingProblem title="This review page couldn’t be found" message="Please check the link and try again." />;
  if (!slug) return missing;
  if (page.isPending) return <BookingLoading rows={3} />;
  if (page.isError) {
    return page.error instanceof BookingApiError && page.error.status === 404 ? (
      missing
    ) : (
      <BookingProblem title="This review page couldn’t be loaded" message="Please try again in a moment." onRetry={() => void page.refetch()} />
    );
  }
  if (isOtherSchool(ctx.businessId, page.data.school)) return missing;

  if (submit.isSuccess) {
    return (
      <div className="mx-auto max-w-lg space-y-4 rounded-lg border bg-card p-8 text-center">
        <h1 className="text-2xl font-semibold">Your Review has been Submitted</h1>
        <p className="text-muted-foreground">
          Thanks for the review, you’re the best! You have been a great support to us and our community by leaving a review.
        </p>
        <Button asChild>
          <a href={ctx.links.home()}>Home</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[560px] space-y-6 py-4">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold">Leave Us a Review</h1>
        <p className="text-muted-foreground">We would love you to share your experience with us, our events and our products.</p>
      </div>
      {!takesReviews(page.data) ? (
        <Notice tone="info">This school isn’t taking reviews yet.</Notice>
      ) : (
        <>
          {submit.isError && <Notice tone="error">{messageOf(submit.error, FAILED)}</Notice>}
          <SubmitReviewForm page={page.data} submitting={submit.isPending} onSubmit={(v) => submit.mutate(v)} />
        </>
      )}
    </div>
  );
}

function SubmitReviewForm({
  page,
  submitting,
  onSubmit,
}: {
  page: SubmitReviewPage;
  submitting: boolean;
  onSubmit: (values: SubmitReviewValues) => void;
}) {
  const [values, setValues] = useState(() => emptySubmitReview(page));
  const [errors, setErrors] = useState<SubmitReviewErrors>({});
  const [tried, setTried] = useState(false);
  const hasCourses = (page.courses?.length ?? 0) > 0;
  const hasProducts = (page.products?.length ?? 0) > 0;
  const choices = choicesFor(page, values.targetType);

  function update(patch: Partial<SubmitReviewValues>) {
    const next = { ...values, ...patch };
    setValues(next);
    if (tried) setErrors(validateSubmitReview(next, page));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTried(true);
    const found = validateSubmitReview(values, page);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(values);
  }

  const kinds: Array<{ value: ReviewTargetType; label: string }> = [
    ...(hasCourses ? [{ value: "course" as const, label: "I want to leave a review for an event I attended" }] : []),
    ...(hasProducts ? [{ value: "product" as const, label: "I want to leave a review for a product I purchased" }] : []),
  ];

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {kinds.length > 1 && (
        <fieldset className="space-y-2">
          <legend className="sr-only">What would you like to review?</legend>
          {kinds.map((k) => (
            <label key={k.value} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="review-type"
                value={k.value}
                checked={values.targetType === k.value}
                // Another kind's item can't stay picked.
                onChange={() => update({ targetType: k.value, targetId: "" })}
                className="size-4 accent-primary"
              />
              {k.label}
            </label>
          ))}
          {errors.targetType && <p className="text-sm text-destructive">{errors.targetType}</p>}
        </fieldset>
      )}
      {values.targetType && (
        <Field id="review-target" label={values.targetType === "course" ? "Which event did you attend?" : "Which product did you purchase?"} error={errors.targetId}>
          <select
            id="review-target"
            value={values.targetId}
            onChange={(e) => update({ targetId: e.target.value })}
            aria-invalid={!!errors.targetId}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="" disabled>
              Please select
            </option>
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="submit-review-first-name" label="First Name" error={errors.firstName}>
          <Input
            id="submit-review-first-name"
            autoComplete="given-name"
            value={values.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            aria-invalid={!!errors.firstName}
          />
        </Field>
        <Field id="submit-review-last-name" label="Last Name" error={errors.lastName}>
          <Input
            id="submit-review-last-name"
            autoComplete="family-name"
            value={values.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            aria-invalid={!!errors.lastName}
          />
        </Field>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="submit-review-first-name-only"
          checked={values.showOnlyFirstName}
          onCheckedChange={(c) => update({ showOnlyFirstName: c === true })}
        />
        <Label htmlFor="submit-review-first-name-only">Only show first name</Label>
      </div>
      <Field id="submit-review-email" label="Email Address" error={errors.email}>
        <Input
          id="submit-review-email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={(e) => update({ email: e.target.value })}
          aria-invalid={!!errors.email}
        />
      </Field>
      <div>
        <p className="mb-2 text-sm font-medium">Your Rating</p>
        <RatingInput value={values.rating} onChange={(rating) => update({ rating })} />
        {errors.rating && <p className="mt-1 text-sm text-destructive">{errors.rating}</p>}
      </div>
      <Field id="submit-review-text" label="Your Review" error={errors.text}>
        <Textarea
          id="submit-review-text"
          rows={5}
          value={values.text}
          onChange={(e) => update({ text: e.target.value })}
          aria-invalid={!!errors.text}
        />
      </Field>
      <div className="flex justify-center">
        <Button type="submit" disabled={submitting} className="w-full max-w-[270px]">
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitting ? "Submitting…" : "Leave Review"}
        </Button>
      </div>
    </form>
  );
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block">
        {label}
      </Label>
      {children}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}
